import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import { WAITLIST_PREFERENCE_TYPES } from 'dpl/shared/constants/breeder_app';
import {
  FILTER_SIZE,
  FILTER_COAT,
  FILTER_GENERATION,
  FILTER_AGE,
  FILTER_EXTRAS,
  FILTER_TAIL
} from 'dpl/constants/query_params';
import { BREED_TYPES } from 'dpl/components/BreedSearch/util';

import Divider from './Divider';
import GenderFilter from './GenderFilter';
import BreederBreedHealthFilter from './BreederBreedHealthFilter';
import MultiSelectChecklistFilter from './MultiSelectChecklistFilter';
import ColorFilter from './ColorFilter';
import PriceFilter from './PriceFilter';
import SimilarBreedsFilter from './SimilarBreedsFilter';

const { SIZE } = WAITLIST_PREFERENCE_TYPES;

/** @param {any} props */
export default function SearchFilters({ className = 'ph4 pb10' }) {
  const server = useSelector(state => state.server);
  const {
    breedSizes,
    breedCoat,
    breedGeneration,
    breedColors,
    breedTail,
    ageOptions,
    breedType,
    akcRegistrationOptions
  } = server;

  const hasSizePreapplied = Boolean(server.preappliedFilters?.[SIZE]);
  const shouldDisplaySizeFilter = breedSizes?.length > 0;
  const shouldDisplayCoatTypeFilter = breedCoat?.length > 0;
  const shouldDisplayGenerationFilter = breedGeneration?.length > 0;
  const shouldDisplayColorFilter = breedColors?.length > 0;
  const shouldDisplayTailTypeFilter = breedTail?.length > 0;
  const shouldDisplayAkcFilter = breedType === BREED_TYPES.PUREBRED;

  return (
    <div className={classnames('SearchFilters', className)}>
      <SimilarBreedsFilter />
      <GenderFilter />
      {shouldDisplaySizeFilter && !hasSizePreapplied && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Size"
            filterParam={FILTER_SIZE}
            filterOptions={breedSizes}
          />
        </>
      )}
      {shouldDisplayCoatTypeFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Coat type"
            filterParam={FILTER_COAT}
            filterOptions={breedCoat}
          />
        </>
      )}
      {shouldDisplayColorFilter && (
        <>
          <Divider />
          <ColorFilter title="Color" filterOptions={breedColors} />
        </>
      )}
      {shouldDisplayGenerationFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Generation"
            filterParam={FILTER_GENERATION}
            filterOptions={breedGeneration}
            filterTooltipText="Generation refers to the percentage of each parent dog present in that specific litter."
          />
        </>
      )}
      <Divider />
      <MultiSelectChecklistFilter
        filterTitle="Age"
        filterParam={FILTER_AGE}
        filterOptions={ageOptions}
      />
      <Divider />
      <BreederBreedHealthFilter
        title="Health testing"
        subtitle="Evaluates breeding dogs for inherited and physical conditions to help prevent disease and promote lifelong health of puppies."
      />
      <Divider />
      <PriceFilter />
      {shouldDisplayAkcFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="AKC Registration"
            filterSubtitle="AKC-recognized pedigree and lineage."
            filterParam={FILTER_EXTRAS}
            filterOptions={akcRegistrationOptions}
          />
        </>
      )}
      {shouldDisplayTailTypeFilter && (
        <>
          <Divider />
          <MultiSelectChecklistFilter
            filterTitle="Tail type"
            filterSubtitle="Choose between docked (shortened) or natural full-length tails."
            filterParam={FILTER_TAIL}
            filterOptions={breedTail}
          />
        </>
      )}
    </div>
  );
}

SearchFilters.propTypes = {
  className: PropTypes.string
};
